import type { ICellProps } from '@creditflowresearch/uilib/Table/DataTable';
import { RegionCell } from '@/creditflowresearch/ui/common/components/DataCells';
import SizeCell from '@/creditflowresearch/ui/components/bondscreener/Cells/SizeCell';
import ParentCell from '@/creditflowresearch/ui/components/bondscreener/Cells/ParentCell';
import MoneySizeCell from '@/creditflowresearch/ui/components/bondscreener/Cells/MoneySizeCell';
import StatusCell from '@/creditflowresearch/ui/components/bondscreener/Cells/StatusCell';
import DealDateCell from '@/creditflowresearch/ui/components/bondscreener/Cells/DealDateCell';
import IssuerTickerCell from '@/creditflowresearch/ui/components/bondscreener/Cells/IssuerTickerCell';
import TenorCell from '@/creditflowresearch/ui/components/bondscreener/Cells/TenorCell';

import type { IColumnRendererEntry } from '@/creditflowresearch/ui/common/components/DataTable/columns';

import RegionsCell from '@/creditflowresearch/ui/components/bondscreener/Cells/RegionsCell';
import {
  leadsEntry, truncatedEntry, truncatedDictionaryEntry,
} from '@/creditflowresearch/ui/components/bondscreener/Table/columns/sharedColumnCells';

import IndustryCell from '../../Cells/IndustryCell';
import IssuersListCell from '../../Cells/IssuersListCell';
import MarketCell from '../../Cells/MarketCell';

const columnRendererRegistry: Readonly<Record<string, IColumnRendererEntry>> = {
  date: {
    dataType: 'date',
    width: 90,
    minWidth: 90,
    maxWidth: 90,
    locked: true,
    cellCallback: ({ row }: ICellProps) => (<DealDateCell date={ row.date } statusId={ row.statusId } />),
  },

  industry: {
    dataType: 'badge',
    width: 80,
    minWidth: 75,
    maxWidth: 80,
    cellCallback: ({ row }: ICellProps) => (<IndustryCell industryId={ row.industryId } subindustryId={ row.subindustryId } />),
  },

  region: {
    dataType: 'abbreviation',
    width: 70,
    minWidth: 70,
    maxWidth: 70,
    cellCallback: ({ row }: ICellProps) => (<RegionCell regionId={ row.regionId } countryId={ row.countryId } />),
  },

  status: {
    dataType: 'status',
    width: 70,
    minWidth: 70,
    maxWidth: 70,
    cellCallback: ({ row }: ICellProps) => (<StatusCell statusId={ row.statusId } />),
  },

  issuer: {
    dataType: 'text',
    width: 230,
    minWidth: 230,
    maxWidth: 230,
    cellCallback: ({ row }: ICellProps) => (<IssuersListCell value={ row.issuer ? [row.issuer] : null } />),
  },

  issuers: {
    dataType: 'text',
    width: 230,
    minWidth: 230,
    maxWidth: 230,
    cellCallback: ({ row }: ICellProps) => (<IssuersListCell value={ row.issuers } />),
  },

  tenor: {
    dataType: 'number',
    width: 70,
    minWidth: 70,
    maxWidth: 70,
    cellCallback: ({ row }: ICellProps) => (<TenorCell tenor={ row.tenor } perpetual={ row.perpetual } />),
  },

  ticker: {
    dataType: 'abbreviation',
    width: 80,
    minWidth: 70,
    maxWidth: 100,
    cellCallback: ({ row }: ICellProps) => (<IssuerTickerCell ticker={ row.ticker } />),
  },

  parent: {
    dataType: 'text',
    width: 120,
    minWidth: 100,
    maxWidth: 200,
    cellCallback: ({ row }: ICellProps) => (<ParentCell parent={ row.parent } />),
  },

  additionalRegions: {
    dataType: 'text',
    width: 100,
    minWidth: 80,
    maxWidth: 150,
    cellCallback: ({ row }: ICellProps) => (<RegionsCell regionIds={ row.additionalRegions } />),
  },
  activeLeads: leadsEntry('activeLeads'),
  coManagers: leadsEntry('coManagers'),
  jointLeads: leadsEntry('jointLeads'),
  leftLead: leadsEntry('leftLead'),
  passiveLeads: leadsEntry('passiveLeads'),
  bookAndDelivery: leadsEntry('bookAndDelivery'),
  diCoordinators: leadsEntry('diCoordinators'),
  coordinatingManagers: leadsEntry('coordinatingManagers', { width: 140, minWidth: 100, maxWidth: 180 }),
  guarantors: truncatedEntry('guarantors'),
  guidance: truncatedEntry('guidance', { width: 100, minWidth: 80, maxWidth: 130 }),
  spreadSet: truncatedEntry('spreadSet', { width: 100, minWidth: 80, maxWidth: 130 }),
  launch: truncatedEntry('launch', { width: 120, minWidth: 100, maxWidth: 160 }),
  pricing: truncatedEntry('pricing', { width: 120, minWidth: 100, maxWidth: 160 }),
  security: truncatedEntry('security', { width: 120, minWidth: 100, maxWidth: 160 }),
  minDenomination: truncatedEntry('minDenomination', { width: 100, minWidth: 90, maxWidth: 150 }),
  trancheComments: truncatedEntry('trancheComments', { width: 150, minWidth: 120, maxWidth: 250 }),
  useOfProceeds: truncatedEntry('useOfProceeds'),
  bookUpdates: truncatedEntry('bookUpdates'),
  ranking: truncatedDictionaryEntry('rankingId', 'rankings', { width: 111, minWidth: 111, maxWidth: 111 }),
  subindustry: truncatedDictionaryEntry('subindustryId', 'subindustries', { width: 100, minWidth: 80, maxWidth: 130 }),
  listing: truncatedDictionaryEntry('listingId', 'listings'),
  market: {
    dataType: 'text',
    width: 100,
    minWidth: 80,
    maxWidth: 150,
    cellCallback: ({ row }: ICellProps) => (<MarketCell marketIds={ row.marketIds } />),
  },
  currency: { dataType: 'dictionary', dictionaryKey: 'currencies', displayField: 'displayName', width: 80, minWidth: 50, maxWidth: 80 },
  couponType: { dataType: 'dictionary', dictionaryKey: 'couponTypes', width: 100, minWidth: 85, maxWidth: 100, customAlign: 'left' },
  benchmark: { dataType: 'dictionary', dictionaryKey: 'benchmarks', width: 100, minWidth: 80, maxWidth: 120, customAlign: 'left' },
  sizeDescription: { dataType: 'dictionary', dictionaryKey: 'sizeDescriptions', width: 100, minWidth: 80, maxWidth: 140, customAlign: 'left' },
  country: truncatedDictionaryEntry('countryId', 'countries', { width: 100, minWidth: 90, maxWidth: 140 }),
  debut: { dataType: 'dictionary', dictionaryKey: 'debutTypes', width: 70, minWidth: 60, maxWidth: 100, customAlign: 'left' },
  esg: truncatedDictionaryEntry('esgId', 'esgTypes', { width: 80, minWidth: 70, maxWidth: 100 }),
  registration: truncatedDictionaryEntry('registrationId', 'registrations', { width: 100, minWidth: 80, maxWidth: 150 }),
  uop: { dataType: 'dictionary', dictionaryKey: 'uopTypes', width: 100, minWidth: 80, maxWidth: 140, customAlign: 'left' },
  segment: { dataType: 'dictionary', dictionaryKey: 'marketSegments', width: 70, minWidth: 60, maxWidth: 90 },
  product: { dataType: 'dictionary', dictionaryKey: 'products', width: 70, minWidth: 60, maxWidth: 90 },
  spread: truncatedEntry('spread', { width: 80, minWidth: 70, maxWidth: 100 }),
  ratingMoodys: { dataType: 'text', width: 80, minWidth: 50, maxWidth: 80, customAlign: 'right' },
  ratingSnP: { dataType: 'text', width: 80, minWidth: 50, maxWidth: 80, customAlign: 'right' },
  ratingFitch: { dataType: 'text', width: 80, minWidth: 50, maxWidth: 80, customAlign: 'right' },
  ratingScope: { dataType: 'text', width: 50, minWidth: 50, maxWidth: 60, customAlign: 'right' },
  newIssueConcession: { dataType: 'text', width: 70, minWidth: 70, maxWidth: 70 },
  iptPxd: truncatedEntry('iptPxd', { width: 70, minWidth: 70, maxWidth: 70 }),
  backendBenchmark: truncatedEntry('backendBenchmark', { width: 80, minWidth: 70, maxWidth: 100 }),
  initialPriceTalk: truncatedEntry('initialPriceTalk', { width: 80, minWidth: 70, maxWidth: 100 }),
  roadshowDates: truncatedEntry('roadshowDates', { width: 100, minWidth: 80, maxWidth: 130 }),
  timing: truncatedEntry('timing', { width: 80, minWidth: 70, maxWidth: 130 }),
  tradingLevel: { dataType: 'text', width: 80, minWidth: 70, maxWidth: 100 },
  collateral: truncatedEntry('collateral', { width: 100, minWidth: 80, maxWidth: 150 }),
  figi: { dataType: 'text', width: 90, minWidth: 80, maxWidth: 120 },
  maturityDate: { dataType: 'date' },
  pricingDate: { dataType: 'date' },
  announcedDate: { dataType: 'date' },
  lastUpdatedDate: { dataType: 'date' },
  callDate: { dataType: 'date' },
  firstPayDate: { dataType: 'date' },
  settlementDate: { dataType: 'date' },
  resetDate: { dataType: 'date' },
  parCallDate: { dataType: 'date', width: 110, minWidth: 100, maxWidth: 130 },
  issuePrice: { dataType: 'number', decimals: 6, width: 120, minWidth: 120, maxWidth: 160 },
  yield: { dataType: 'percent', decimals: 3, width: 80, minWidth: 70, maxWidth: 100 },
  bookXCovered: { dataType: 'number', decimals: 2, width: 70, minWidth: 70, maxWidth: 70 },
  bookSize: { dataType: 'number', rounded: true, thousandsSeparator: ' ', width: 80, minWidth: 80, maxWidth: 100 },
  backendSpread: { dataType: 'number', rounded: true, thousandsSeparator: ' ', width: 80, minWidth: 70, maxWidth: 100 },
  call: { dataType: 'number', decimals: 2, width: 70, minWidth: 60, maxWidth: 80 },
  mwcTPlus: { dataType: 'number', decimals: 1, width: 70, minWidth: 60, maxWidth: 80 },
  numberOfTranches: { dataType: 'number', rounded: true, width: 60, minWidth: 50, maxWidth: 70 },
  parAmount: { dataType: 'number', decimals: 1, width: 80, minWidth: 70, maxWidth: 100 },
  parCallMonths: { dataType: 'number', rounded: true, width: 70, minWidth: 60, maxWidth: 80 },
  peakBook: { dataType: 'number', rounded: true, thousandsSeparator: ' ', width: 80, minWidth: 70, maxWidth: 100 },
  tradingDelta: { dataType: 'number', decimals: 1, width: 80, minWidth: 70, maxWidth: 100 },
  coupon: { dataType: 'percent', decimals: 3, width: 80, minWidth: 70, maxWidth: 100 },
  bookAttrition: { dataType: 'percent', decimals: 2, width: 70, minWidth: 70, maxWidth: 70 },
  announcedSize: { dataType: 'money', width: 90, minWidth: 80, maxWidth: 120, cellCallback: ({ row }: ICellProps) => (<MoneySizeCell value={ row.announcedSize } currencyId={ row.currencyId } decimals={ 0 } />) },
  totalDealSize: { dataType: 'money', width: 90, minWidth: 80, maxWidth: 120, cellCallback: ({ row }: ICellProps) => (<MoneySizeCell value={ row.totalDealSize } currencyId={ row.currencyId } decimals={ 0 } />) },
  estimatedDealSize: { dataType: 'money', decimals: 0, width: 100, minWidth: 90, maxWidth: 130 },
  sizeAfterTap: { dataType: 'money', decimals: 0, width: 90, minWidth: 80, maxWidth: 120 },
  size: {
    dataType: 'money',
    width: 70,
    minWidth: 70,
    maxWidth: 140,
    cellCallback: ({ row }: ICellProps) => (
      <SizeCell
        value={ row.size }
        currencyId={ row.currencyId }
        statusId={ row.statusId }
      />
    ),
  },

  upsized: { dataType: 'boolean', emptyFalse: true, width: 70, minWidth: 60, maxWidth: 70 },
  regRights: { dataType: 'boolean', nullLabel: 'None' },
  bailIn: { dataType: 'boolean' },
  coc: { dataType: 'boolean' },
  di: { dataType: 'boolean' },
  droppedTranche: { dataType: 'boolean', width: 80, minWidth: 70, maxWidth: 80 },
  hybrid: { dataType: 'boolean' },
  marketing: { dataType: 'boolean' },
  noGrow: { dataType: 'boolean', width: 70, minWidth: 60, maxWidth: 70 },
  perpetual: { dataType: 'boolean' },
  reOpenTap: { dataType: 'boolean' },
  smr: { dataType: 'boolean' },
  verified: { dataType: 'boolean' },
  benchmarkSize: { dataType: 'boolean', minWidth: 120 },
  par: { dataType: 'boolean' },
  nonCountable: { dataType: 'boolean', width: 100, minWidth: 80, maxWidth: 110 },
};

export const LOCKED_COLUMN_IDS: ReadonlySet<string> = new Set(
  Object.entries(columnRendererRegistry)
    .filter(([, entry]) => entry.locked)
    .map(([id]) => id),
);

export default columnRendererRegistry;
